import { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from '@iconify/react/dist/iconify.js';
import { PaginationLimit } from "../../../../components/PaginationLimit";
import SearchableDropdown from '../../../../components/SearchableDropdown';
import { getComparator, stableSort } from '../../../../utils/helpers/universalFunctions';
import Loader from "../../../../components/Looder";
import { deleteCity, getCities } from "../../../../redux/actions/SocietyController/CtrlLocationAction/CtrlLocationAction";
import DeleteConfirmModal from "./DeleteConfirmModal";
import AddCityModal from "./AddCityModal";
import EditCityModal from "./EditCityModal";

const STATUS_OPTIONS = [
    { value: 'all', label: 'All Status' },
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' },
];

const COLUMNS = [
    { id: "slNo", label: "#", sort: false, width: "60px" },
    { id: "id", label: "ID", sort: true, width: "100px" },
    { id: "city", label: "City Name", sort: true, width: "200px" },
    { id: "state", label: "State", sort: true, width: "150px" },
    { id: "country", label: "Country", sort: true, width: "150px" },
    { id: "status", label: "Status", sort: true, width: "100px" },
    { id: "action", label: "Actions", sort: false, width: "100px" },
];

export const CityTable = ({ addCityModalOpen = false, setAddCityModalOpen = () => { }, searchQuery = {}, setSearchQuery = () => { } }) => {
    const dispatch = useDispatch();
    const { loading, cityData } = useSelector(state => state.CtrlLocationReducers);

    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState(null);
    const [search, setSearch] = useState('');
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [editCityModalOpen, setEditCityModalOpen] = useState({ status: false, items: null });

    const handleSort = (field) => {
        if (!field) return;
        const isAsc = orderBy === field && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(field);
    };
    const handleSearchSubmit = () => setSearchQuery((prev) => ({ ...prev, page: 1, search }));
    const handleClear = () => { setSearch(''); setSearchQuery({ page: 1, limit: 10, search: '', status: 'all' }); };
    const handlePageChange = (cp) => { if (searchQuery.page !== cp) setSearchQuery({ ...searchQuery, page: cp }); };
    const handleLimitChange = (e) => setSearchQuery({ ...searchQuery, page: 1, limit: e.target.value });

    const getCityTableData = () => dispatch(getCities({
        page: searchQuery.page || 1,
        limit: searchQuery.limit || 10,
        search: searchQuery.search || '',
        status: searchQuery.status === 'all' ? null : searchQuery.status,
    }));

    const handleConfirmDelete = () => {
        setIsDeleting(true);
        dispatch(deleteCity({
            id: itemToDelete?.id,
            onSuccess: () => {
                setDeleteModalOpen(false);
                setItemToDelete(null);
                setIsDeleting(false);
                getCityTableData();
            },
        }));
    };

    const rows = useMemo(() => {
        const offset = ((cityData?.meta?.pagination?.page || 1) - 1) * (cityData?.meta?.pagination?.limit || 10);
        return (cityData?.data || []).map((item, index) => ({
            slNo: index + 1 + offset,
            id: item.city_id,
            city: item.name,
            state: item.state?.name,
            country: item.country || 'N/A',
            status: item.is_active,
            raw: item,
        }));
    }, [cityData]);

    const sortedRows = useMemo(
        () => (orderBy ? stableSort(rows, getComparator(order, orderBy)) : rows),
        [rows, order, orderBy]
    );

    return (
        <>
            <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-bg-gray-50/40">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    <div className="tw-relative">
                        <Icon icon="ion:search-outline" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                        <input
                            type="text"
                            placeholder="Search city..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
                            className="tw-h-8 tw-pl-8 tw-pr-3 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500 focus:tw-ring-1 focus:tw-ring-rose-500 tw-w-56"
                        />
                    </div>
                    <div className="tw-w-40">
                        <SearchableDropdown
                            value={searchQuery?.status || 'all'}
                            onChange={(v) => setSearchQuery((prev) => ({ ...prev, page: 1, status: v }))}
                            options={STATUS_OPTIONS}
                            placeholder="Status"
                            clearable={false}
                            small
                        />
                    </div>
                    <button
                        onClick={handleClear}
                        className="tw-h-8 tw-inline-flex tw-items-center tw-px-3 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50"
                    >
                        Clear
                    </button>
                </div>
                <button
                    onClick={getCityTableData}
                    className="tw-h-8 tw-w-8 tw-inline-flex tw-items-center tw-justify-center tw-rounded-md tw-text-gray-500 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50"
                    title="Refresh"
                >
                    <Icon icon="mdi:refresh" className={`tw-text-base ${loading ? 'tw-animate-spin' : ''}`} />
                </button>
            </div>

            <div className="tw-overflow-x-auto">
                <table className="tw-w-full tw-text-xs">
                    <thead>
                        <tr className="tw-bg-gradient-to-r tw-from-slate-50 tw-to-gray-50">
                            {COLUMNS.map((col) => {
                                const sortable = col.sort !== false;
                                const isActive = orderBy === col.id;
                                return (
                                    <th
                                        key={col.id}
                                        onClick={() => sortable && handleSort(col.id)}
                                        style={{ minWidth: col.width || 'auto' }}
                                        className={`tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px] ${sortable ? 'tw-cursor-pointer hover:tw-text-rose-600' : ''}`}
                                    >
                                        <span className="tw-inline-flex tw-items-center tw-gap-1">
                                            {col.label}
                                            {sortable && (
                                                <Icon
                                                    icon={isActive
                                                        ? (order === 'asc' ? 'mdi:menu-up' : 'mdi:menu-down')
                                                        : 'mdi:unfold-more-horizontal'}
                                                    className={isActive ? 'tw-text-rose-500' : 'tw-text-gray-300'}
                                                />
                                            )}
                                        </span>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody className="tw-divide-y tw-divide-gray-50">
                        {loading ? (
                            <tr><td colSpan={COLUMNS.length} className="tw-text-center tw-py-12"><Loader /></td></tr>
                        ) : sortedRows.length > 0 ? (
                            sortedRows.map((c) => (
                                <tr key={c.id} className="hover:tw-bg-slate-50/50 tw-transition-colors">
                                    <td className="tw-px-4 tw-py-2.5 tw-text-gray-400">{c.slNo}</td>
                                    <td className="tw-px-4 tw-py-2.5 tw-text-rose-600 tw-font-medium tw-font-mono">#{c.id}</td>
                                    <td className="tw-px-4 tw-py-2.5">
                                        <div className="tw-flex tw-items-center tw-gap-2">
                                            <Icon icon="mdi:city-variant-outline" className="tw-text-blue-400 tw-text-base" />
                                            <span className="tw-font-medium tw-text-gray-800">{c.city}</span>
                                        </div>
                                    </td>
                                    <td className="tw-px-4 tw-py-2.5 tw-text-gray-600">{c.state || '—'}</td>
                                    <td className="tw-px-4 tw-py-2.5 tw-text-gray-600">{c.country || '—'}</td>
                                    <td className="tw-px-4 tw-py-2.5">
                                        <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium tw-border ${
                                            c.status === 1 || c.status === true
                                                ? 'tw-bg-emerald-50 tw-text-emerald-600 tw-border-emerald-100'
                                                : 'tw-bg-gray-50 tw-text-gray-500 tw-border-gray-100'
                                        }`}>
                                            <span className={`tw-w-1.5 tw-h-1.5 tw-rounded-full ${(c.status === 1 || c.status === true) ? 'tw-bg-emerald-500' : 'tw-bg-gray-400'}`}></span>
                                            {(c.status === 1 || c.status === true) ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="tw-px-4 tw-py-2.5">
                                        <div className="tw-flex tw-items-center tw-gap-1">
                                            <button
                                                onClick={() => setEditCityModalOpen({ status: true, items: c.raw })}
                                                className="tw-p-1 tw-rounded tw-bg-blue-50 tw-text-blue-600 hover:tw-bg-blue-100 tw-transition-colors"
                                                title="Edit"
                                            >
                                                <Icon icon="lucide:edit" className="tw-text-sm" />
                                            </button>
                                            <button
                                                onClick={() => { setItemToDelete({ id: c.id, name: c.city }); setDeleteModalOpen(true); }}
                                                className="tw-p-1 tw-rounded tw-bg-red-50 tw-text-red-500 hover:tw-bg-red-100 tw-transition-colors"
                                                title="Delete"
                                            >
                                                <Icon icon="mingcute:delete-2-line" className="tw-text-sm" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={COLUMNS.length} className="tw-text-center tw-py-12">
                                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                        <Icon icon="mdi:city-variant-outline" className="tw-text-3xl tw-text-gray-300" />
                                        <p className="tw-text-xs tw-text-gray-400">No cities found</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {cityData?.data?.length > 0 && (
                <div className="tw-border-t tw-border-gray-100 tw-px-3 tw-py-2">
                    <PaginationLimit
                        currentPage={cityData?.meta?.pagination?.page || 1}
                        totalRecords={cityData?.meta?.pagination?.total}
                        limit={cityData?.meta?.pagination?.limit || 10}
                        handleLimitChange={handleLimitChange}
                        handlePageChange={handlePageChange}
                    />
                </div>
            )}

            <DeleteConfirmModal
                open={deleteModalOpen}
                handleClose={() => { setDeleteModalOpen(false); setItemToDelete(null); setIsDeleting(false); }}
                handleConfirm={handleConfirmDelete}
                title="Delete City"
                itemName={itemToDelete?.name}
                itemId={itemToDelete?.id}
                isLoading={isDeleting}
            />
            <AddCityModal
                open={addCityModalOpen}
                getCityTableData={getCityTableData}
                handleClose={() => setAddCityModalOpen(false)}
            />
            <EditCityModal
                open={editCityModalOpen?.status}
                editData={editCityModalOpen?.items}
                getCityTableData={getCityTableData}
                handleClose={() => setEditCityModalOpen({})}
            />
        </>
    );
};
